/**
 * 서비스 팝업 — 지금 띄울 한 장을 고르고(`pickServicePopup`) 그 장의 글을 짓는다(`toServicePopupCopy`).
 *
 * ```
 *   차례  ① 체험 끝나 감(끝 3일 전부터 · 하루 한 번) → ② 초기 설정 안 함(하루 한 번) → ③ 계정 연동 끊김(7일에 한 번)
 * ```
 * 🔑 한 번에 한 장이다 — 해당하는 것 가운데 차례가 앞서고 빈도가 허락하는 첫 장. 빈도는 마지막으로 띄운 때(`lastShown`)로 잰다.
 * 🔑 서비스 틀(로그인 판)이 어느 화면에서든 머문 지 30초 뒤에 고른다(`useServicePopup`).
 * ⚠️ 계산은 훅이 아니라 여기 있다 — DOM 없이 검사할 수 있어야 한다.
 */

import { paths } from 'src/routes/paths';

import { trialBonusText } from 'src/utils/plan/plan-trial-banner';
import { isPlanTrackAreaLocked } from 'src/utils/plan/plan-track-gate';

import { toStatusLinkHref } from 'src/sections/status/utils/status-row-target';
import { computeOnboardingChecklist } from 'src/sections/onboarding/utils/onboarding-state';

import { SERVICE_POPUP_COPY, SERVICE_POPUP_TRIAL_DAYS, SERVICE_POPUP_ACCOUNT_GAP_MS } from './service-popup-constants';

/** 팝업 갈래 — 차례도 이 순서다 */
export type ServicePopupKind = 'trial' | 'setup' | 'account';

/** 고른 한 장과 그 재료 */
export type ServicePopup =
  | { kind: 'trial'; daysLeft: number; endMs: number; bonusMonths: number }
  | { kind: 'setup' }
  | { kind: 'account'; accounts: string[] };

/** 갈래마다 마지막으로 띄운 때(밀리초) — 이 브라우저에 남긴 값 */
export type ServicePopupShown = Partial<Record<ServicePopupKind, number>>;

/**
 * 지금 띄울 한 장 — 없으면 `null`.
 * @param input.trial 체험 남은 날 · 끝 시각(체험 플랜이 아니면 `null`)
 * @param input.bonusMonths 체험 중 결제하면 얹어 주는 달 수 — 이미 받았으면 0
 * @param input.setup 시작하기 `pending` · 추적이 열린 플랜인데 아무것도 걸지 않았다
 * @param input.brokenAccounts 연동이 끊긴(Unipile `FAILED`) 인스타그램 자사 계정
 * @param input.lastShown 갈래마다 마지막으로 띄운 때
 * @param input.now 기준 시각
 */
export function pickServicePopup(input: {
  trial: { daysLeft: number; endMs: number } | null;
  bonusMonths: number;
  setup: boolean;
  brokenAccounts: readonly string[];
  lastShown: ServicePopupShown;
  now: number;
}): ServicePopup | null {
  const { trial, lastShown, now } = input;
  if (trial && trial.daysLeft <= SERVICE_POPUP_TRIAL_DAYS && !shownToday(lastShown.trial, now)) {
    return { kind: 'trial', daysLeft: trial.daysLeft, endMs: trial.endMs, bonusMonths: input.bonusMonths };
  }
  if (input.setup && !shownToday(lastShown.setup, now)) return { kind: 'setup' };
  if (input.brokenAccounts.length > 0 && !(lastShown.account !== undefined && now - lastShown.account < SERVICE_POPUP_ACCOUNT_GAP_MS)) {
    return { kind: 'account', accounts: [...input.brokenAccounts] };
  }
  return null;
}

/**
 * 초기 설정을 아직 안 했나 — 추적이 열린 플랜인데 브랜드 · 키워드 · 자사 계정을 하나도 걸지 않았다.
 * 팝업의 초기 설정 장과 홈 머리의 「초기 설정하기」 칩이 같은 판정을 쓴다.
 * @param doc 워크스페이스 문서
 * @param campaigns 타겟 키워드 세트
 * @param planConfig 플랜 기능표(`usePlanAccess`)
 */
export function isTrackingSetupBlank(doc: Record<string, any> | null, campaigns: readonly Record<string, any>[], planConfig: Parameters<typeof isPlanTrackAreaLocked>[0]): boolean {
  const trackingPlan = !(['brandKeyword', 'targetKeyword', 'brandAccount'] as const).every((area) => isPlanTrackAreaLocked(planConfig, area));
  const brandKeywords: string[] = Array.isArray(doc?.brandSetting?.brandKeyword) ? doc.brandSetting.brandKeyword : [];
  const accounts: unknown[] = Array.isArray(doc?.brandSetting?.brandAccounts) ? doc.brandSetting.brandAccounts : [];
  return trackingPlan && computeOnboardingChecklist({ brandKeywords, campaigns: [...campaigns] }).done === 0 && accounts.length === 0;
}

/** 팝업 한 장의 글 — 화면 글 그대로 */
export type ServicePopupCopy = {
  title: string;
  body: string;
  /** 「체험 중 결제 혜택」 상자 — 혜택이 없으면 `null` */
  offer: { label: string; title: string } | null;
  /** 설정 네 걸음 — 초기 설정 장만 */
  steps: readonly { name: string; hint: string }[];
  /** 끊긴 계정 — 앞 두 개만 줄로 · 나머지는 「외 N개」 */
  accounts: { shown: string[]; more: number };
  close: string;
  primary: { label: string; href: string };
  /** 「다시 보지 않기」 — 초기 설정 장만 */
  never: string | null;
};

/**
 * @param popup 고른 한 장
 * @returns 그 장의 글 · 단추가 가는 곳
 */
export function toServicePopupCopy(popup: ServicePopup): ServicePopupCopy {
  const empty = { steps: [], accounts: { shown: [], more: 0 }, never: null };
  if (popup.kind === 'trial') {
    const copy = SERVICE_POPUP_COPY.trial;
    const end = new Date(popup.endMs);
    const bonus = popup.bonusMonths > 0 ? trialBonusText(popup.bonusMonths) : '';
    return {
      ...empty,
      title: popup.daysLeft > 0 ? `체험이 ${popup.daysLeft}일 남았어요` : '체험이 오늘 끝나요',
      body: popup.daysLeft > 0 ? `${end.getMonth() + 1}월 ${end.getDate()}일에 끝나요.` : '',
      offer: bonus ? { label: copy.offerLabel, title: `지금 결제하면 ${bonus} 추가` } : null,
      close: copy.close,
      primary: { label: bonus ? copy.primary : copy.primaryPlain, href: `${paths.subscription.plan}?type=upgrade` },
    };
  }
  if (popup.kind === 'setup') {
    const copy = SERVICE_POPUP_COPY.setup;
    return { ...empty, title: copy.title, body: copy.body, offer: null, steps: copy.steps, close: copy.close, primary: { label: copy.primary, href: paths.service.start }, never: copy.never };
  }
  const copy = SERVICE_POPUP_COPY.account;
  return {
    ...empty,
    title: copy.title,
    body: copy.body,
    offer: null,
    accounts: { shown: popup.accounts.slice(0, ACCOUNT_ROWS), more: Math.max(popup.accounts.length - ACCOUNT_ROWS, 0) },
    close: copy.close,
    primary: { label: copy.primary, href: toStatusLinkHref() },
  };
}

/**
 * 팝업이 설 수 있는 화면인가 — 서비스 화면(`/service` 아래)만. 관리자 · 계정 화면처럼 같은 틀을 빌려 쓰는 곳은 뺀다.
 * @param pathname 지금 주소의 경로
 */
export function isServicePopupPath(pathname: string | null | undefined): boolean {
  const path = String(pathname ?? '');
  return path === paths.service.root || path.startsWith(`${paths.service.root}/`);
}

/**
 * 이 화면에서 뺄 장을 지운 재료 — 그 일을 하고 있는 화면에서는 그 장을 띄우지 않는다.
 * 추적 관리(`/service/tracking` 아래)에서는 초기 설정 · 계정 연동 장을 뺀다.
 * @param facts 고르는 재료
 * @param pathname 지금 주소의 경로
 */
export function toPathPopupFacts<T extends { setup: boolean; brokenAccounts: readonly string[] }>(facts: T, pathname: string | null | undefined): T {
  const path = String(pathname ?? '');
  const tracking = path === paths.service.tracking.root || path.startsWith(`${paths.service.tracking.root}/`);
  return tracking ? { ...facts, setup: false, brokenAccounts: [] } : facts;
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 끊긴 계정 줄 수 — 넘치면 「외 N개」 */
const ACCOUNT_ROWS = 2;

/** 이 브라우저의 같은 날에 이미 띄웠나 */
function shownToday(at: number | undefined, now: number): boolean {
  if (at === undefined) return false;
  return new Date(at).toDateString() === new Date(now).toDateString();
}
